<div class="xr-page dashboard">
  <header class="xr-page-header">
    <div>
      <h1 class="xr-page-title">{{ 'dashboard.title' | translate }}</h1>
    </div>
    <div class="dashboard-refresh">
      <app-company-filter></app-company-filter>
      @if (lastUpdated) {
        <span>{{ 'dashboard.updated_at' | translate }} {{ lastUpdated | date: 'HH:mm' }}</span>
      }
      <button type="button" class="xr-btn xr-btn--secondary" (click)="refreshData()" [disabled]="isLoading">
        <mat-icon>refresh</mat-icon>
        {{ 'dashboard.refresh' | translate }}
      </button>
    </div>
  </header>

  @if (errorKey) {
    <div class="dashboard-error" role="alert">
      <mat-icon>error_outline</mat-icon>
      <span>{{ errorKey | translate }}</span>
    </div>
  }

  @if (isLoading) {
    <div class="dashboard-loading">
      <mat-spinner diameter="40"></mat-spinner>
      <p>{{ 'dashboard.loading' | translate }}</p>
    </div>
  } @else {
    <section class="dashboard-stats" [attr.aria-label]="'dashboard.summary' | translate">
      <button type="button" class="dashboard-stat" (click)="openRooms()">
        <span class="dashboard-stat-icon"><mat-icon>meeting_room</mat-icon></span>
        <span class="dashboard-stat-text">
          <span class="dashboard-stat-label">{{ 'dashboard.rooms_label' | translate }}</span>
          <strong>{{ rooms.length }}</strong>
          <small>{{ activeRoomCount }} {{ 'dashboard.waiting_short' | translate }} · {{ busyRoomCount }} {{ 'dashboard.busy_short' | translate }}</small>
        </span>
        <mat-icon class="dashboard-stat-chevron">chevron_right</mat-icon>
      </button>

      @if (devicesAvailable) {
        <button type="button" class="dashboard-stat" (click)="openDevices()">
          <span class="dashboard-stat-icon"><mat-icon>smartphone</mat-icon></span>
          <span class="dashboard-stat-text">
            <span class="dashboard-stat-label">{{ 'dashboard.devices_label' | translate }}</span>
            <strong>{{ deviceCount }}</strong>
            <small>{{ enabledDeviceCount }} {{ 'dashboard.enabled_short' | translate }} · {{ pendingDeviceCount }} {{ 'dashboard.pending_short' | translate }}</small>
          </span>
          <mat-icon class="dashboard-stat-chevron">chevron_right</mat-icon>
        </button>
      }

      @if (techniciansAvailable) {
        <button type="button" class="dashboard-stat" (click)="openTechnicians()">
          <span class="dashboard-stat-icon"><mat-icon>engineering</mat-icon></span>
          <span class="dashboard-stat-text">
            <span class="dashboard-stat-label">{{ 'dashboard.technicians_label' | translate }}</span>
            <strong>{{ technicianCount }}</strong>
            <small>{{ 'dashboard.registered_short' | translate }}</small>
          </span>
          <mat-icon class="dashboard-stat-chevron">chevron_right</mat-icon>
        </button>
      }
    </section>

    <section class="dashboard-grid">
      <div class="xr-card dashboard-rooms">
        <div class="dashboard-card-head">
          <h2 class="xr-card-title">{{ 'dashboard.available_rooms_title' | translate }}</h2>
          <button type="button" class="dashboard-link" (click)="openRooms()">
            {{ 'dashboard.view_all_f' | translate }} <mat-icon>chevron_right</mat-icon>
          </button>
        </div>

        @if (rooms.length) {
          <div class="dashboard-table-wrap">
            <table class="dashboard-table">
              <thead>
                <tr>
                  <th>{{ 'dashboard.room_name_header' | translate }}</th>
                  <th>{{ 'dashboard.room_technician_header' | translate }}</th>
                  @if (showCompany) {
                    <th>{{ 'dashboard.room_company_header' | translate }}</th>
                  }
                  <th>{{ 'dashboard.room_status_header' | translate }}</th>
                  <th><span class="xr-sr-only">{{ 'dashboard.actions' | translate }}</span></th>
                </tr>
              </thead>
              <tbody>
                @for (room of rooms; track room.id) {
                  <tr>
                    <td class="dashboard-room-name">{{ room.name || ('dashboard.not_available' | translate) }}</td>
                    <td>
                      @if (room.technician) {
                        {{ room.technician.name }} {{ room.technician.lastname }}
                      } @else {
                        —
                      }
                    </td>
                    @if (showCompany) {
                      <td>{{ room.company?.name || '—' }}</td>
                    }
                    <!-- Status and people in the room in one column: with the company column the table no longer fitted -->
                    <td class="dashboard-room-status">
                      <span class="xr-badge" [ngClass]="getRoomStatusClass(room)">{{ getRoomStatusLabelKey(room) | translate }}</span>
                      <small>{{ room.connectionNumber || 0 }} / 2</small>
                    </td>
                    <td class="dashboard-room-action">
                      @if (canJoinRoom(room)) {
                        <button type="button" class="xr-btn xr-btn--small" [ngClass]="joinButtonClass(room)" (click)="openRoom(room)">{{ 'dashboard.join_room' | translate }}</button>
                      } @else {
                        <button type="button" class="xr-btn" disabled>{{ (isRoomBusy(room) ? 'dashboard.room_status_busy' : 'dashboard.join_room') | translate }}</button>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <div class="dashboard-room-cards">
            @for (room of rooms; track room.id) {
              <article class="dashboard-room-card">
                <div class="dashboard-room-card-top">
                  <strong>{{ room.name }}</strong>
                  <span class="xr-badge" [ngClass]="getRoomStatusClass(room)">{{ getRoomStatusLabelKey(room) | translate }}</span>
                </div>
                @if (room.technician) {
                  <span class="dashboard-muted">{{ room.technician.name }} {{ room.technician.lastname }}@if (showCompany && room.company) { · {{ room.company.name }}}</span>
                }
                <div class="dashboard-room-card-bottom">
                  <span class="dashboard-muted">{{ room.connectionNumber || 0 }} / 2</span>
                  @if (canJoinRoom(room)) {
                    <button type="button" class="xr-btn xr-btn--small" [ngClass]="joinButtonClass(room)" (click)="openRoom(room)">{{ 'dashboard.join_room' | translate }}</button>
                  } @else {
                    <button type="button" class="xr-btn" disabled>{{ (isRoomBusy(room) ? 'dashboard.room_status_busy' : 'dashboard.join_room') | translate }}</button>
                  }
                </div>
              </article>
            }
          </div>
        } @else {
          <p class="xr-empty">{{ 'dashboard.no_rooms' | translate }}</p>
        }
      </div>

      <aside class="dashboard-side">
        @if (devicesAvailable) {
          <div class="xr-card dashboard-side-card">
            <div class="dashboard-card-head">
              <h2 class="xr-card-title">{{ 'dashboard.devices_label' | translate }}</h2>
              <button type="button" class="dashboard-link" (click)="openDevices()">{{ 'dashboard.view_all' | translate }}</button>
            </div>
            <button type="button" class="dashboard-tile dashboard-tile--wait" (click)="openDevices()">
              <mat-icon>schedule</mat-icon>
              <span><small>{{ 'dashboard.awaiting_approval' | translate }}</small><strong>{{ pendingDeviceCount }}</strong></span>
              <mat-icon>chevron_right</mat-icon>
            </button>
            <button type="button" class="dashboard-tile dashboard-tile--ok" (click)="openDevices()">
              <mat-icon>check_circle</mat-icon>
              <span><small>{{ 'dashboard.enabled_devices' | translate }}</small><strong>{{ enabledDeviceCount }}</strong></span>
              <mat-icon>chevron_right</mat-icon>
            </button>
          </div>
        }

        @if (conversationsAvailable) {
          <div class="xr-card dashboard-side-card">
            <div class="dashboard-card-head">
              <h2 class="xr-card-title">{{ 'dashboard.recent_conversations' | translate }}</h2>
              <button type="button" class="dashboard-link" (click)="openConversations()">{{ 'dashboard.view_all_f' | translate }}</button>
            </div>
            @for (call of recentConversations; track call.uuid) {
              <button type="button" class="dashboard-call" (click)="openConversations()">
                <span class="dashboard-call-icon"><mat-icon>description</mat-icon></span>
                <span class="dashboard-call-text">
                  <strong>{{ call.room_name }}</strong>
                  <small>{{ call.technician_name }} · {{ call.message_count }} {{ 'conversations.phrases' | translate }}</small>
                </span>
                <small class="dashboard-muted">{{ call.started_at | date: 'dd/MM HH:mm' }}</small>
              </button>
            } @empty {
              <p class="dashboard-muted dashboard-side-empty">{{ 'dashboard.no_conversations' | translate }}</p>
            }
          </div>
        }
      </aside>
    </section>
  }
</div>
